import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {Discount} from "crqlar-react-common/dist/models/billing/invoiceItem.model";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {lineAmounts} from "./folioLine.util";

type DiscountType = 'amount' | 'percentage';

interface Props {
    /** The line to discount; mount the dialog only while there is one. */
    line: FolioItem
    currency: string
    onApply: (discounts: Discount[]) => void
    onClose: () => void
}

/** One discount per line: an amount off (at most the line's price) or a percentage. */
const FolioDiscountDialog = ({line, currency, onApply, onClose}: Props) => {
    const {t} = useTranslation();
    const existing = line.discounts?.[0];
    const [type, setType] = useState<DiscountType>(existing?.percentOff != null ? 'percentage' : 'amount');
    const [value, setValue] = useState(() => {
        if (existing?.percentOff != null) return String(existing.percentOff);
        return existing?.amountCentsOff != null ? String(existing.amountCentsOff / 100) : '';
    });

    const {unitCents, quantity} = lineAmounts(line);
    const maxAmount = (unitCents * quantity) / 100;
    const number = parseFloat(value || '0');
    const percentInvalid = type === 'percentage' && (number < 0 || number > 100);
    const amountInvalid = type === 'amount' && number > maxAmount;
    const invalid = !!value && (percentInvalid || amountInvalid);

    const apply = () => {
        if (number <= 0 || invalid) return;
        onApply([type === 'percentage' ? {percentOff: number} : {amountCentsOff: Math.round(number * 100)}]);
    };

    return (
        <Dialog open onClose={onClose} maxWidth={'sm'} fullWidth>
            <DialogTitle>{existing ? t('Edit Discount') : t('Add Discount')}</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{pt: 1}}>
                    <ToggleButtonGroup
                        value={type}
                        exclusive
                        onChange={(_event, next: DiscountType | null) => {
                            if (!next) return;
                            setType(next);
                            setValue('');
                        }}
                        aria-label={t('Discount')}
                        size={'small'}
                        sx={{width: '100%'}}
                    >
                        <ToggleButton value={'amount'} sx={{flex: 1}}>{t('Amount')}</ToggleButton>
                        <ToggleButton value={'percentage'} sx={{flex: 1}}>{t('Percentage')}</ToggleButton>
                    </ToggleButtonGroup>

                    <TextField
                        fullWidth
                        autoFocus
                        size={'small'}
                        type={'number'}
                        value={value}
                        onChange={event => setValue(event.target.value)}
                        onKeyDown={event => {
                            if (event.key === 'Enter') apply();
                        }}
                        placeholder={type === 'percentage' ? t('Percentage off') : t('Amount off')}
                        InputProps={{endAdornment: type === 'percentage' ? '%' : currency}}
                        error={invalid}
                        helperText={percentInvalid ? t('Percentage must be between 0 and 100')
                            : amountInvalid ? t('Amount cannot exceed item price') : ''}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('Cancel')}</Button>
                {existing && (
                    <Button onClick={() => onApply([])} color={'error'} variant={'outlined'}>
                        {t('Remove')}
                    </Button>
                )}
                <Button onClick={apply} variant={'contained'} disabled={!value || number <= 0 || invalid}>
                    {existing ? t('Update') : t('Apply')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FolioDiscountDialog;
